// Universal Touch Dwell & Acceptance Delay Engine for Motor Tremors, Cerebral Palsy & Ataxia

export interface TouchDwellConfig {
  enabled: boolean;
  delayMs: number; // e.g. 300ms to 1500ms
}

class TouchDwellEngine {
  private config: TouchDwellConfig = {
    enabled: false,
    delayMs: 600,
  };

  private activeTimeout: number | null = null;
  private activeTarget: HTMLElement | null = null;
  private progressRingElement: SVGSVGElement | null = null;

  constructor() {
    this.createProgressRingElement();
  }

  public setConfig(newConfig: Partial<TouchDwellConfig>) {
    this.config = { ...this.config, ...newConfig };
    if (!this.config.enabled) {
      this.cancelDwell();
    }
  }

  public getConfig(): TouchDwellConfig {
    return { ...this.config };
  }

  private createProgressRingElement() {
    if (typeof document === 'undefined') return;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '52');
    svg.setAttribute('height', '52');
    svg.setAttribute('viewBox', '0 0 52 52');
    svg.style.position = 'fixed';
    svg.style.pointerEvents = 'none';
    svg.style.zIndex = '99999';
    svg.style.display = 'none';
    svg.style.transform = 'translate(-50%, -50%)';

    const bgCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    bgCircle.setAttribute('cx', '26');
    bgCircle.setAttribute('cy', '26');
    bgCircle.setAttribute('r', '22');
    bgCircle.setAttribute('fill', 'rgba(28, 27, 31, 0.7)');
    bgCircle.setAttribute('stroke', 'rgba(255, 255, 255, 0.3)');
    bgCircle.setAttribute('stroke-width', '4');

    const progressCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    progressCircle.setAttribute('cx', '26');
    progressCircle.setAttribute('cy', '26');
    progressCircle.setAttribute('r', '22');
    progressCircle.setAttribute('fill', 'none');
    progressCircle.setAttribute('stroke', '#ffd64e');
    progressCircle.setAttribute('stroke-width', '5');
    progressCircle.setAttribute('stroke-dasharray', '138.23');
    progressCircle.setAttribute('stroke-dashoffset', '138.23');
    progressCircle.setAttribute('stroke-linecap', 'round');
    progressCircle.style.transform = 'rotate(-90deg)';
    progressCircle.style.transformOrigin = '50% 50%';
    progressCircle.setAttribute('id', 'dwell-progress-indicator');

    svg.appendChild(bgCircle);
    svg.appendChild(progressCircle);
    document.body.appendChild(svg);
    this.progressRingElement = svg;
  }

  public handlePointerDown(e: React.PointerEvent, onConfirm: () => void) {
    if (!this.config.enabled || this.config.delayMs <= 0) {
      onConfirm();
      return;
    }

    this.cancelDwell();

    const target = e.currentTarget as HTMLElement;
    this.activeTarget = target;
    const x = e.clientX;
    const y = e.clientY;

    if (this.progressRingElement) {
      this.progressRingElement.style.left = `${x}px`;
      this.progressRingElement.style.top = `${y}px`;
      this.progressRingElement.style.display = 'block';

      const progressCircle = this.progressRingElement.querySelector('#dwell-progress-indicator') as SVGElement;
      if (progressCircle) {
        progressCircle.style.transition = 'none';
        progressCircle.setAttribute('stroke-dashoffset', '138.23');
        // Trigger reflow
        void progressCircle.getBoundingClientRect();
        progressCircle.style.transition = `stroke-dashoffset ${this.config.delayMs}ms linear`;
        progressCircle.setAttribute('stroke-dashoffset', '0');
      }
    }

    this.activeTimeout = window.setTimeout(() => {
      this.cancelDwell();
      // Visual pulse
      target.classList.add('scale-95');
      setTimeout(() => target.classList.remove('scale-95'), 150);

      // Play soft confirmation tick
      this.playConfirmTick();
      onConfirm();
    }, this.config.delayMs);
  }

  public cancelDwell() {
    if (this.activeTimeout !== null) {
      clearTimeout(this.activeTimeout);
      this.activeTimeout = null;
    }
    if (this.progressRingElement) {
      this.progressRingElement.style.display = 'none';
      const progressCircle = this.progressRingElement.querySelector('#dwell-progress-indicator') as SVGElement;
      if (progressCircle) {
        progressCircle.style.transition = 'none';
        progressCircle.setAttribute('stroke-dashoffset', '138.23');
      }
    }
    this.activeTarget = null;
  }

  private playConfirmTick() {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.08);
    } catch (e) {}
  }
}

export const touchDwellEngine = new TouchDwellEngine();
